import { extractRecipeSizeLabel } from "@/lib/manufacturing/groupMembers";
import type { MfgRecipeKind, MfgRecipeSummary } from "@/lib/manufacturing/types";
import type { ComposeConfirmedLink, ComposeNode } from "./types";

export type ComposeMatchSuggestion = {
  recipe_id: string;
  code: string;
  name: string;
  kind: MfgRecipeKind;
  score: number;
  band: "exact" | "strong" | "fuzzy";
};

function norm(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, " ");
}

function codeNorm(s: string): string {
  return s.trim().toUpperCase().replace(/\s+/g, "");
}

/**
 * Rank org recipes for a draft component/product node.
 * Pure client heuristic; boosts + confirmed slot links improve remaining SKUs.
 */
export function suggestMatchesForNode(
  node: ComposeNode,
  recipes: readonly MfgRecipeSummary[],
  opts?: {
    boosts?: readonly string[];
    confirmedLinks?: readonly ComposeConfirmedLink[];
    limit?: number;
    kindFilter?: MfgRecipeKind[];
  },
): ComposeMatchSuggestion[] {
  const limit = opts?.limit ?? 8;
  const boosts = (opts?.boosts ?? []).map(norm).filter(Boolean);
  const links = opts?.confirmedLinks ?? [];
  const kindFilter = opts?.kindFilter;
  const needleCode = codeNorm(node.code);
  const needleName = norm(node.name);
  const size = node.sizeLabel;
  const slot = node.slotKey;
  const variant = node.variantKey;
  const style = node.styleKey;

  const scored: ComposeMatchSuggestion[] = [];

  for (const r of recipes) {
    if (!r.is_active && node.role === "product") {
      // still allow matching inactive for link-on-commit of shells
    }
    if (kindFilter && !kindFilter.includes(r.kind)) continue;

    let score = 0;
    let band: ComposeMatchSuggestion["band"] = "fuzzy";
    const rCode = codeNorm(r.code);
    const rName = norm(r.name);

    if (rCode === needleCode) {
      score += 100;
      band = "exact";
    } else if (rCode.includes(needleCode) || needleCode.includes(rCode)) {
      score += 40;
      band = "strong";
    }

    if (rName === needleName) {
      score += 50;
      if (band === "fuzzy") band = "exact";
    } else if (rName.includes(needleName) || needleName.includes(rName)) {
      score += 20;
      if (band === "fuzzy") band = "strong";
    }

    if (size) {
      const rSize = extractRecipeSizeLabel(r);
      if (rSize === size) score += 25;
      else if (rCode.includes(size.toUpperCase()) || rName.includes(size.toLowerCase())) {
        score += 10;
      }
    }

    if (slot) {
      const slotToken = slot.replace(/_/g, "-");
      if (r.tags.some((t) => t.toLowerCase() === `slot:${slot}`)) score += 30;
      if (rCode.includes(slotToken.toUpperCase()) || rName.includes(slot.replace(/_/g, " "))) {
        score += 12;
      }
    }

    if (variant) {
      if (r.tags.some((t) => t.toLowerCase() === `variant:${variant}`)) score += 20;
      if (rName.includes(variant) || rCode.includes(variant.toUpperCase())) score += 8;
    }

    if (node.kind === r.kind) score += 5;

    for (const b of boosts) {
      if (rName.includes(b) || norm(r.code).includes(b)) score += 15;
    }

    // Prior confirmed links in this plan (after partial commits / manual matches).
    // Wrong-size siblings of the same slot get only a light affinity so they
    // don't drown out size-appropriate catalog hits.
    for (const link of links) {
      if (link.recipe_id !== r.id && codeNorm(link.code) !== rCode) continue;
      const sizeMismatch = Boolean(size && link.sizeLabel && link.sizeLabel !== size);
      if (sizeMismatch) {
        if (slot && link.slotKey === slot) score += 8;
        continue;
      }
      let linkScore = 0;
      if (slot && link.slotKey === slot) linkScore += 50;
      if (size && link.sizeLabel === size) linkScore += 35;
      if (variant && link.variantKey === variant) linkScore += 20;
      if (style && link.styleKey === style) linkScore += 15;
      if (linkScore > 0) {
        score += linkScore;
        if (band === "fuzzy") band = "strong";
        if (linkScore >= 85) band = "exact";
      }
    }

    if (score <= 0) continue;
    scored.push({
      recipe_id: r.id,
      code: r.code,
      name: r.name,
      kind: r.kind,
      score,
      band,
    });
  }

  scored.sort((a, b) => b.score - a.score || a.code.localeCompare(b.code));
  return scored.slice(0, limit);
}

export function applyMatchToNode(
  node: ComposeNode,
  recipeId: string | null,
  theoretical: boolean,
): ComposeNode {
  if (theoretical) return { ...node, match: { status: "theoretical" } };
  if (!recipeId) return { ...node, match: { status: "unmatched" } };
  return { ...node, match: { status: "matched", recipe_id: recipeId } };
}

export function reclassifyNode(node: ComposeNode, kind: MfgRecipeKind): ComposeNode {
  return { ...node, kind };
}

export function updateNodeInPlan(
  nodes: readonly ComposeNode[],
  nodeId: string,
  patch: (n: ComposeNode) => ComposeNode,
): ComposeNode[] {
  return nodes.map((n) => (n.id === nodeId ? patch(n) : n));
}
